/*
 * Restore points in front of risky work, as the API reports them
 * (docs/api.md, "Restore points before risky work"). A restore point is an
 * operation with `followUp`: the work waiting on it, whether that work was
 * dispatched, and why it was canceled. Everything here reads that; nothing
 * is remembered in the browser except which notices a viewer dismissed.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSyncExternalStore } from "react";
import { useOrg } from "../app/org";
import { api, isApiError } from "./client";
import { keys } from "./keys";
import { watchOperation } from "./live";
import { isActiveOp, type Operation } from "./types";

/**
 * A chain that starts by installing a PHP version on the server (the API's services/php-versions.ts) rather than with
 * a restore point: the same `followUp`, but nothing to continue without and nothing to dismiss.
 */
export const isPhpFirst = (op: Pick<Operation, "kind">) => op.kind === "server.php.install";

/**
 * A deploy to Test that copies Live first (the API's services/copy-first.ts): the copy carries the deploy in the same
 * `followUp`. Nothing to continue without either: when the copy fails, deploy again without it.
 */
export const isCopyFirst = (op: Pick<Operation, "kind" | "followUp">) => op.kind === "env.clone" && !!op.followUp;

/** "Deploy 0d1a969 to Live" → "Deploying 0d1a969 to Live" (the API's labels are imperative). */
export function progressive(label: string): string {
  const [first = "", ...rest] = label.split(" ");
  const ing = /e$/.test(first) && !/ee$/.test(first) ? `${first.slice(0, -1)}ing` : `${first}ing`;
  return [ing, ...rest].join(" ");
}

/** Canceled restore points are worth showing for this long (the API's alert window too). */
const SHOW_CANCELLED_MS = 3 * 86_400_000;

/* Per-viewer convenience: notices this person dismissed. */
const DISMISSED_KEY = "hx-dismissed-restore-points";
function loadDismissed(): string[] {
  try {
    return JSON.parse(localStorage.getItem(DISMISSED_KEY) ?? "[]") as string[];
  } catch {
    return [];
  }
}
let dismissed = loadDismissed();
const listeners = new Set<() => void>();
export function dismissRestorePoint(opId: string) {
  dismissed = [...dismissed.slice(-99), opId];
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(dismissed));
  } catch {
    /* not fatal: it just shows again next visit */
  }
  listeners.forEach((l) => l());
}
function useDismissed(): string[] {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => dismissed,
  );
}

export type ChainState =
  /** The restore point is being taken, or it's done and the work is about to start. */
  | { kind: "running"; op: Operation }
  /** The restore point failed (or the work no longer made sense): nothing was changed. */
  | { kind: "cancelled"; op: Operation };

/** What an environment's newest restore point says, if it needs showing. */
export function useRestorePointState(envId: string | null | undefined, ops: Operation[] | undefined): ChainState | null {
  const gone = useDismissed();
  if (!envId || !ops) return null;
  const op = ops.find((o) => o.environmentId === envId && o.followUp && !isPhpFirst(o));
  // A database tool's restore point (type "db_edit") shows where the change was made (pages/sites/env/DatabaseEdit.tsx).
  if (!op?.followUp || op.followUp.type === "db_edit") return null;
  if (isActiveOp(op.status) || (op.followUp.status === "waiting" && op.status === "succeeded")) return { kind: "running", op };
  if (op.followUp.status === "cancelled") {
    const at = Date.parse(op.finishedAt ?? op.createdAt);
    if (!gone.includes(op.id) && Date.now() - at < SHOW_CANCELLED_MS) return { kind: "cancelled", op };
  }
  return null;
}

/** "Continue without a restore point" for a canceled one, from any tab: runs its work, audit-logged. */
export function useContinueRestorePoint(siteId: string) {
  const org = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (backupId: string) => api.post<{ operationId: string }>(`/orgs/${org.id}/sites/${siteId}/backups/${backupId}/continue`),
    onSuccess: (res) => {
      watchOperation(res.operationId);
      void qc.invalidateQueries({ queryKey: keys.site(org.id, siteId) });
      void qc.invalidateQueries({ queryKey: keys.operationLists(org.id) });
    },
  });
}

/** The 409 a chained route answers when the server's backup storage isn't ready. */
export const isRestorePointUnavailable = (e: unknown) => isApiError(e) && e.code === "restore_point_unavailable";
